import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { UserInteractionOutcome } from '@nimbus-library/features/agent-assistant/models/agent-assistant.model';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { IconWeight } from '@nimbus-library/features/shared/directives/icon-weight.enum';
import { ChatServiceType, IChatMessage } from '@nimbus-global/features/chat/models/chat.model';
import { ChatMediaPolicy } from '@nimbus-global/features/chat/models/chat-service.model';
import { ChatInputMessageComponent } from '@nimbus-global/features/chat/components/chat-input-message/chat-input-message.component';
import { isChatInputDisabledForTaskState } from '@nimbus-global/features/chat/utils/chat-enabled.utils';

@Component({
  selector: 'nimbus-chat-widget',
  standalone: false,
  templateUrl: './chat-widget.component.html',
  styleUrl: './chat-widget.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatWidgetComponent {
  @ViewChild(ChatInputMessageComponent)
  private chatInputMessage: ChatInputMessageComponent;

  @Input()
  public messages: IChatMessage[] = [];
  @Input()
  public taskState: UserInteractionOutcome;
  @Input()
  public unparkDisabled: boolean = false;
  @Input()
  public unparkTooltip: TranslationKeys;
  @Input()
  public hideInput: boolean = false;
  @Input()
  public chatType: ChatServiceType;
  @Input()
  public emptyTemplate: TemplateRef<unknown>;
  @Input()
  public authorInfoTemplate: TemplateRef<IChatMessage>;
  @Input()
  public messageExtensionTemplate: TemplateRef<{ message: IChatMessage }>;
  @Input()
  public messageActionTemplate: TemplateRef<{ message: IChatMessage }>;
  @Input()
  public showActions = false;
  @Input()
  public hideAuthorTime = false;
  @Input()
  public hasMoreMessages = false;
  @Input()
  public inputDisabled = false;
  @Input()
  public saveDraftApiUrl: string;
  @Input()
  public conversationId: string;
  @Input()
  public enableDraft = false;
  @Input()
  public hasPendingAction = false;
  @Input()
  public pendingActionHint: TranslationKeys;
  @Input()
  public isMediaHandlingEnabled: boolean | null = false;
  @Input()
  public mediaPolicy: ChatMediaPolicy | null = null;
  @Input()
  public isCustomerChat = true;
  @Input()
  public showStopButton = false;

  @Output()
  public copyTextInitialized = new EventEmitter<string>();
  @Output()
  public stopRequested = new EventEmitter<void>();
  @Output()
  public messageSent = new EventEmitter<string>();
  @Output()
  public attachmentsSent = new EventEmitter<File[]>();
  @Output()
  public voiceMessageSent = new EventEmitter<Blob>();
  @Output()
  public loadPrevMessagesRequested = new EventEmitter<void>();
  @Output()
  public unparkRequested = new EventEmitter<void>();

  public TranslationKeys = TranslationKeys;
  public readonly ICONS = ICONS;
  public readonly IconWeight = IconWeight;

  public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public get showParkedBanner(): boolean {
    return this.taskState === UserInteractionOutcome.Parked;
  }

  public get isDragAndDropSupported(): boolean {
    return (
      !this.hideInput &&
      !!this.isMediaHandlingEnabled &&
      !this.inputDisabled &&
      !this.hasPendingAction &&
      !isChatInputDisabledForTaskState(this.taskState)
    );
  }

  public onFilesDropped(files: FileList): void {
    this.chatInputMessage?.addAttachments(Array.from(files));
  }

  public onCopyTextInitialized(text: string): void {
    this.copyTextInitialized.next(text);
  }

  public onUnpark(): void {
    if (this.unparkDisabled) {
      return;
    }
    this.unparkRequested.emit();
  }

  public onMessageSent(message: string): void {
    this.messageSent.emit(message);
  }

  public onStopRequested(): void {
    this.stopRequested.emit();
  }

  public onAttachmentsSent(files: File[]): void {
    this.attachmentsSent.emit(files);
  }

  public onVoiceMessageSent(blob: Blob): void {
    this.voiceMessageSent.emit(blob);
  }

  public onLoadPrevMessagesRequested(): void {
    this.loadPrevMessagesRequested.emit();
  }
}
